import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Filter} from '@github-ui/filter'
import {ContextualGlobalSsoBanner} from '@github-ui/global-sso-banner-ui/contextual'
import {PreviewCardOutlet} from '@github-ui/preview-card'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {DashboardSidebar} from '@github-ui/shared-dashboard-sidebar/DashboardSidebar'
import {ShortcutEditingHeader} from '@github-ui/saved-views'
import {useMemo} from 'react'

import {issuesDashboardSurfaceLayoutRoute} from './issues-dashboard-surface-layout-route'
import {issuesDashboardSurfaceContentRoute} from './issues-dashboard-surface-content-route'
import {SharedHeader} from '@github-ui/shared-dashboard-surface/components/shared/SharedHeader'
import {SharedLayout} from '@github-ui/shared-dashboard-surface/components/shared/SharedLayout'
import {NewIssueButton} from '../components/NewIssueButton'
import {
  IssuesDashboardSurfaceStateProvider,
  useIssuesDashboardSurfaceStateContext,
} from '../contexts/IssuesDashboardSurfaceStateContext'
import {useIssuesDashboardSurfaceLayoutState} from '../hooks/use-issues-dashboard-surface-layout-state'

import type {IssuesDashboardLayoutPayload} from '../types/routes-types'

export function IssuesDashboardSurfaceLayoutComponent() {
  const {data: payload, isPending: isLoading} = useRouteQuery(issuesDashboardSurfaceLayoutRoute, 'mainQuery')

  return <IssuesDashboardSurfaceLayoutRoot payload={payload} isLoading={isLoading} />
}

export function IssuesDashboardSurfaceLayoutRoot({
  payload,
  isLoading,
}: {
  payload: IssuesDashboardLayoutPayload | undefined
  isLoading: boolean
}) {
  return (
    <AnalyticsProvider
      appName="issues-dashboard-surface"
      category="Dashboard Surface"
      metadata={{page: 'issues-dashboard'}}
    >
      <ContextualGlobalSsoBanner />
      <div data-testid="issues-dashboard-surface-layout" data-issue-and-pr-hovercards-enabled>
        {payload && (
          <IssuesDashboardSurfaceStateProvider payload={payload} isLoading={isLoading}>
            <IssuesDashboardSurfaceLayout />
          </IssuesDashboardSurfaceStateProvider>
        )}
        <PreviewCardOutlet />
      </div>
    </AnalyticsProvider>
  )
}

function IssuesDashboardSurfaceLayout() {
  const {savedViews, selectedSidebarLink, editing, deleting, methods, issuesFilterProps, sidebarLinks} =
    useIssuesDashboardSurfaceLayoutState()

  const {fetchedQuery, isLoading, payload} = useIssuesDashboardSurfaceStateContext()

  const savedViewsEnabled = true
  const feedbackDialogEnabled = true
  const defaultViewEnabled = isFeatureEnabled('issues_dashboard_default_view')

  const visibleLinks = useMemo(() => sidebarLinks, [sidebarLinks])
  const generateLinkPath = (path: string | undefined, _query?: string) => {
    return issuesDashboardSurfaceContentRoute.generatePath({mode: path || ''})
  }

  return (
    <SharedLayout
      header={
        editing.shortcut ? (
          <ShortcutEditingHeader
            renderFilter={filterProps => (
              <Filter {...filterProps} {...issuesFilterProps} data-testid="issues-filter-component" />
            )}
            onSave={editing.saveAndDismiss}
            onDismiss={editing.dismiss}
            shortcut={editing.shortcut}
            setShortcutValue={editing.setValue}
            isSaving={editing.isSaving}
            errorMessage={editing.errorMessage}
          />
        ) : (
          <SharedHeader
            dashboardType="issues"
            links={visibleLinks}
            selectedLink={selectedSidebarLink}
            defaultViewId={payload.defaultViewId}
            onSelectLink={methods.selectSidebarLink}
            showFeedbackDialog={feedbackDialogEnabled}
            feedbackSubject="issues_dashboard_feedback"
            feedbackPlaceholderText="Tell us your feedback on the new issues experience"
            filter={<Filter {...issuesFilterProps} data-testid="issues-filter-component" />}
            primaryAction={<NewIssueButton />}
            savedViews={savedViews}
            showSavedViews={savedViewsEnabled}
            onCreateView={methods.createShortcut}
            onEditView={methods.editShortcut}
            onDeleteView={deleting.deleteShortcut}
            onUpdateView={methods.updateShortcut}
            isViewModified={!isLoading && fetchedQuery !== selectedSidebarLink?.query}
            deleteErrorMessage={deleting.errorMessage}
            generateLinkPath={generateLinkPath}
            optOutLocked
            showOptionsButton={
              defaultViewEnabled ? Boolean(selectedSidebarLink) : selectedSidebarLink?.source === 'user'
            }
            showDefaultViewOption={defaultViewEnabled && Boolean(selectedSidebarLink)}
          />
        )
      }
      sidebar={
        <DashboardSidebar
          dashboardType="issues"
          links={visibleLinks}
          selectedLink={selectedSidebarLink?.id || ''}
          onSelectLink={methods.selectSidebarLink}
          savedViews={savedViews}
          showSavedViews={savedViewsEnabled}
          onCreateView={methods.createShortcut}
          generateLinkPath={generateLinkPath}
        />
      }
    />
  )
}
